<script lang="ts" setup>
/**
 * GraphWorkerCanvas — renders Chart.js in a Web Worker via OffscreenCanvas.
 *
 * The chart instance lives entirely in graph.worker.ts. This component:
 *  - Transfers the canvas drawing context to the worker on mount
 *  - Forwards mouse events (mousemove / mouseout / click) to the worker
 *  - Receives tooltip data from the worker and renders the DOM tooltip using
 *    the same externalTooltipHandler from chartjs-graph (with a mock chart)
 *  - Receives resolution / metrics updates from the worker
 *  - Prevents contextmenu directly on the canvas (no worker needed)
 */
import {
	ref,
	watch,
	onMounted,
	onBeforeUnmount,
	nextTick,
	defineExpose,
} from "vue";
import { externalTooltipHandler, documentToOptions } from "chartjs-graph";
import type { AnnotationBubblesPluginOptions } from "chartjs-graph";
import i18n from "@/plugins/i18n";
import DocumentDO = App.Data.Explorer.DocumentDO;
import { useExplorerStore } from "@/modules/explorer/store";
import { useToasterForNotification } from "@/toaster";
import { useLegendActions } from "@/modules/explorer/composables/Graphs/useLegendActions";

const props = defineProps<{
	apiData: any;
	derivedDocument: DocumentDO;
	isDarkMode: boolean;
	isExplorer: boolean;
	isRenderingForGotenberg: boolean;
	graphSettings?: any;
	hierarchyNodes?: Record<number, any>;
	annotationBubblesOptions: AnnotationBubblesPluginOptions | false;
	isTooltipDisabled?: boolean;
}>();

const emit = defineEmits<{
	(e: "resolution", value: { w: number; h: number }): void;
	(e: "blob", value: Blob): void;
}>();

const {
	handleLegendDoubleClick,
	handleHorizontalDoubleClick,
	handleContextMenuAction,
} = useLegendActions(() => props.derivedDocument);

// ---------------------------------------------------------------------------
// Refs
// ---------------------------------------------------------------------------

const canvasRef = ref<HTMLCanvasElement | null>(null);
let worker: Worker | null = null;
let resizeObserver: ResizeObserver | null = null;
let isTransferred = false;

// ---------------------------------------------------------------------------
// Build worker payload (serialisable — no functions)
// ---------------------------------------------------------------------------

const buildPayload = () => {
	// Call documentToOptions on the main thread so that i18n translations
	// (axis titles, legend title, footer text, dimension labels) are resolved
	// here where Vue's i18n context is available. The JSON round-trip strips
	// any functions; the worker uses this as its base options and applies its
	// own worker-specific overrides (tooltip, responsive mode, etc.) on top.
	const mainThreadOptions = documentToOptions(
		props.derivedDocument,
		props.graphSettings ?? null,
		props.isExplorer ?? false,
		props.isDarkMode ?? false,
		props.isRenderingForGotenberg ?? false,
		props.hierarchyNodes ?? {}
	);

	const raw = {
		apiData: props.apiData,
		derivedDocument: props.derivedDocument,
		isDarkMode: props.isDarkMode,
		isExplorer: props.isExplorer,
		isRenderingForGotenberg: props.isRenderingForGotenberg,
		graphSettings: props.graphSettings ?? null,
		hierarchyNodes: props.hierarchyNodes ?? {},
		annotationBubblesOptions: props.annotationBubblesOptions,
		isTooltipDisabled: props.isTooltipDisabled ?? false,
		devicePixelRatio: window.devicePixelRatio,
		mainThreadOptions,
		// Pass locale, fallbackLocale and messages so the worker can call setTranslator and resolve
		// the same translated strings (axis titles, legend title, footer text) with fallback logic.
		locale: i18n.global.locale.value as string,
		fallbackLocale: i18n.global.fallbackLocale.value,
		messages: i18n.global.messages.value,
	};
	// JSON round-trip strips Vue reactive Proxy wrappers (which can't be
	// structured-cloned in some browsers) and any remaining function values.
	return JSON.parse(JSON.stringify(raw));
};

// ---------------------------------------------------------------------------
// Tooltip reconstruction in main thread
// ---------------------------------------------------------------------------

const renderTooltipFromWorker = (payload: any | null) => {
	if (!canvasRef.value) return;
	const canvas = canvasRef.value;

	// Build a mock chart context compatible with externalTooltipHandler
	const mockChart = {
		canvas: {
			parentNode: canvas.parentElement,
			offsetLeft: canvas.offsetLeft,
			offsetTop: canvas.offsetTop,
		},
		data: { datasets: payload?.datasets ?? [] },
		$totalizer: payload?.totalizer ?? null,
	};

	const mockTooltip = payload
		? {
				opacity: payload.opacity ?? 1,
				xAlign: payload.xAlign,
				caretX: payload.caretX,
				caretY: payload.caretY,
				title: payload.title,
				body: payload.body,
				labelColors: payload.labelColors,
				options: {
					bodyFont: { string: payload.options?.bodyFontString ?? "" },
					padding: payload.options?.padding ?? 4,
				},
				dataPoints: payload.dataPoints,
		  }
		: { opacity: 0, body: [] };

	externalTooltipHandler(
		{ chart: mockChart, tooltip: mockTooltip },
		props.derivedDocument,
		props.isDarkMode
	);
};

// ---------------------------------------------------------------------------
// Lifecycle
// ---------------------------------------------------------------------------

onMounted(() => {
	if (!canvasRef.value) return;

	// Prevent right-click context menu and forward coordinates to the worker so it
	// can detect right-clicks on legend items / bar elements and post contextMenuAction.
	canvasRef.value.addEventListener("contextmenu", (e) => {
		e.preventDefault();
		if (!worker || !canvasRef.value) return;
		const rect = canvasRef.value.getBoundingClientRect();
		worker.postMessage({
			type: "event",
			payload: {
				eventType: "contextmenu",
				x: e.clientX - rect.left,
				y: e.clientY - rect.top,
			},
		});
	});

	// Create worker
	worker = new Worker(new URL("./graph.worker.ts", import.meta.url), {
		type: "module",
	});

	// Handle messages from worker
	worker.onmessage = (event: MessageEvent) => {
		const { type, payload } = event.data;
		switch (type) {
			case "tooltip":
				renderTooltipFromWorker(payload);
				break;
			case "resolution":
				emit("resolution", payload);
				break;
			case "blob":
				emit("blob", payload);
				break;
			case "metrics":
				// Update explorer store with highest/lowest metric values from the worker
				if (payload) {
					const store = useExplorerStore();
					store.explorerState.highestMetricValue = payload.highestMetricValue;
					store.explorerState.lowestMetricValue = payload.lowestMetricValue;
				}
				break;
			case "legendSingleClick":
				// Toggle dataset visibility for the clicked legend item
				if (payload?.text) {
					if (!props.derivedDocument?.legend_dimension) {
						useToasterForNotification(
							"Note: Filtering by legend is only possible when a legend is configured.",
							3000,
							"error"
						);
					} else {
						const stripped = payload.text.replace(/\s+$/, "");
						useExplorerStore().toggleHiddenLabel(stripped.toLowerCase());
						// The store mutation updates derivedDocument.hidden_labels in-place, so
						// the prop reference doesn't change and the reactive watch (deep:false)
						// won't fire. Push the updated payload to the worker explicitly.
						nextTick(() => {
							if (worker && isTransferred) {
								worker.postMessage({ type: "update", payload: buildPayload() });
							}
						});
					}
				}
				break;
			case "legendDoubleClick":
				// Apply a filter for the double-clicked legend label
				if (payload?.label) {
					handleLegendDoubleClick(payload.label);
				}
				break;
			case "barDoubleClick":
				// Double-click on a bar element — same filter logic as legend double-click
				if (payload?.label) {
					handleLegendDoubleClick(payload.label);
				}
				break;
			case "xLabelDoubleClick":
				// Double-click on a horizontal axis label — filters on the horizontal
				// dimension instead of the legend dimension. The worker sends the raw
				// label, not the formatted one that was drawn.
				if (payload?.label) {
					handleHorizontalDoubleClick(payload.label);
				}
				break;
			case "contextMenuAction":
				// Right-click on legend or bar — go up hierarchy or remove filter
				handleContextMenuAction();
				break;
		}
	};

	// Size the canvas to the parent container in explicit px instead of using
	// CSS percentages. This avoids containing-block ambiguity for position:absolute.
	const container = canvasRef.value.parentElement ?? canvasRef.value;
	const applySize = (w: number, h: number) => {
		if (!canvasRef.value) return;
		canvasRef.value.style.width = `${w}px`;
		canvasRef.value.style.height = `${h}px`;
	};

	const initialWidth = container.clientWidth;
	const initialHeight = container.clientHeight;
	applySize(initialWidth, initialHeight);

	const offscreen = canvasRef.value.transferControlToOffscreen();
	isTransferred = true;

	worker.postMessage(
		{
			type: "init",
			payload: {
				canvas: offscreen,
				width: initialWidth,
				height: initialHeight,
				...buildPayload(),
			},
		},
		[offscreen]
	);

	// Forward mouse events to worker
	const forwardEvent = (e: MouseEvent) => {
		if (!worker || !canvasRef.value) return;
		const rect = canvasRef.value.getBoundingClientRect();
		worker.postMessage({
			type: "event",
			payload: {
				eventType: e.type,
				x: e.clientX - rect.left,
				y: e.clientY - rect.top,
			},
		});
	};

	canvasRef.value.addEventListener("mousemove", forwardEvent);
	canvasRef.value.addEventListener("mouseout", forwardEvent);
	canvasRef.value.addEventListener("click", forwardEvent);

	// Observe the parent container for size changes, update canvas style and
	// notify the worker so Chart.js can re-render at the new dimensions.
	resizeObserver = new ResizeObserver((entries) => {
		for (const entry of entries) {
			const { width, height } = entry.contentRect;
			applySize(width, height);
			worker?.postMessage({
				type: "resize",
				payload: { width, height },
			});
		}
	});
	resizeObserver.observe(container);
});

onBeforeUnmount(() => {
	resizeObserver?.disconnect();
	resizeObserver = null;
	worker?.postMessage({ type: "destroy" });
	worker?.terminate();
	worker = null;
});

// ---------------------------------------------------------------------------
// Reactivity: send updates when chart data or options change
// ---------------------------------------------------------------------------

watch(
	[
		() => props.apiData,
		() => props.derivedDocument,
		() => props.isDarkMode,
		() => props.annotationBubblesOptions,
		() => props.hierarchyNodes,
		() => props.isTooltipDisabled,
	],
	() => {
		if (worker && isTransferred) {
			worker.postMessage({
				type: "update",
				payload: buildPayload(),
			});
		}
	},
	{ deep: false }
);

const triggerExport = (options: {
	width: number;
	height: number;
	backgroundColor: string;
	showTitle: boolean;
	showSnapshotDate: boolean;
	showChartConfig: boolean;
	graphTitle: string;
}) => {
	if (worker && isTransferred) {
		worker.postMessage({ type: "export", payload: options });
	}
};

defineExpose({ triggerExport });
</script>

<template>
	<canvas ref="canvasRef" style="display: block; position: absolute" />
</template>
